import "./App.css";
import React, { lazy, Suspense, useEffect, useState } from "react";

import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import ScrollToAnchor from "utils/ScrollToAnchor";
import getUserMe_v2 from "services/getUserMe_v2";
import { FREEMIUM_URL } from "services/settings";

const LoginPage = lazy(() => import("pages/Login"));
const RecoverPasswordPage = lazy(() =>
  import("pages/Password/recover").then((module) => ({
    default: module.RecoverPasswordPage,
  }))
);
const ResetPasswordPage = lazy(() =>
  import("pages/Password/reset").then((module) => ({
    default: module.ResetPasswordPage,
  }))
);
const UserRegisterPage = lazy(() =>
  import("pages/UserRegister").then((module) => ({
    default: module.UserRegisterPage,
  }))
);
const UserRegisterWithCode = lazy(() =>
  import("pages/UserRegisterWithCode").then((module) => ({
    default: module.UserRegisterWithCode,
  }))
);
const FreeTest = lazy(() => import("pages/FreeTest"));
const ImpersonateLoginPage = lazy(() =>
  import("pages/ImpersonateLogin").then((module) => ({
    default: module.ImpersonateLoginPage,
  }))
);
const AuthenticatedApp = lazy(() => import("./AuthenticatedApp"));

const RouteLoadingFallback = () => (
  <div
    role="status"
    aria-live="polite"
    style={{
      minHeight: "100vh",
      display: "grid",
      placeItems: "center",
      background: "#0a0e14",
      color: "#8b95a7",
      fontFamily: "sans-serif",
    }}
  >
    Cargando…
  </div>
);

const addStylesheet = (href) => {
  if (document.querySelector(`link[data-deferred-resource="${href}"]`)) {
    return;
  }

  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = href;
  link.dataset.deferredResource = href;
  document.head.appendChild(link);
};

const addScript = (src, { type, noModule } = {}) => {
  if (document.querySelector(`script[data-deferred-resource="${src}"]`)) {
    return;
  }

  const script = document.createElement("script");
  script.src = src;
  script.async = true;
  script.dataset.deferredResource = src;
  if (type) script.type = type;
  if (noModule) script.noModule = true;
  document.head.appendChild(script);
};

const isFreemiumPlan = (data) =>
  String(
    data?.studentPlan?.name ||
      data?.StudentPlan?.Name ||
      data?.studentPlanName ||
      data?.planName ||
      ""
  ).toLowerCase() === "freemium";

const isTrialAccessState = (data) =>
  String(
    data?.subscriptionAccessState || data?.SubscriptionAccessState || ""
  ).toLowerCase() === "trial";

const LOGOUT_GUARD_KEY = "skipFreemiumRedirectAfterLogout";

const isLegacyFreemiumSession = (data) =>
  data?.isFreemiumPlan === true ||
  data?.hasActiveFreemiumPaidTrial === true ||
  isTrialAccessState(data) ||
  isFreemiumPlan(data);

const clearBrowserCookies = () => {
  document.cookie.split(";").forEach((cookieEntry) => {
    const cookieName = cookieEntry.split("=")[0]?.trim();
    if (!cookieName) return;

    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`;
  });
};

function App() {
  const { pathname } = useLocation();
  const logoutRequested = new URLSearchParams(window.location.search).has("logout");
  const isLogoutRoute = window.location.pathname === "/logout";
  const logoutRedirectGuard = window.sessionStorage.getItem(LOGOUT_GUARD_KEY) === "1";
  const [user, setUser] = useState(() => {
    if (logoutRequested || isLogoutRoute || logoutRedirectGuard) {
      return null;
    }

    const loggedUserJson = localStorage.getItem("loggedAppUser");
    if (!loggedUserJson) {
      return null;
    }

    try {
      const parsedUser = JSON.parse(loggedUserJson);
      if (isLegacyFreemiumSession(parsedUser)) {
        window.localStorage.removeItem("loggedAppUser");
        return null;
      }
      return parsedUser;
    } catch (_) {
      window.localStorage.removeItem("loggedAppUser");
      return null;
    }
  });
  const currentUserToken = user?.token;
  const currentStudentLevelName = user?.studentLevelName;
  const currentStudentPlanName = user?.studentPlanName;
  const currentPlanName = user?.planName;
  const currentUserIsFreemium = isFreemiumPlan(user);
  const [isCheckingFreemiumRedirect, setIsCheckingFreemiumRedirect] = useState(
    !logoutRequested &&
      !isLogoutRoute &&
      !logoutRedirectGuard &&
      Boolean(currentUserToken) &&
      Boolean(FREEMIUM_URL)
  );

  useEffect(() => {
    if (pathname === "/register-freemium") {
      return undefined;
    }

    [
      "https://fonts.googleapis.com/icon?family=Material+Icons",
      "https://fonts.googleapis.com/css?family=Roboto:300,400,500,700&display=swap",
      "https://fonts.googleapis.com/css2?family=Barlow:wght@100;300;400;500;600;700;800;900&display=swap",
      "https://fonts.googleapis.com/css2?family=Poppins:wght@200;300;500;600;700;800&display=swap",
      "https://fonts.googleapis.com/css2?family=Roboto+Condensed&display=swap",
    ].forEach(addStylesheet);

    return undefined;
  }, [pathname]);

  useEffect(() => {
    let timeoutId;

    const loadAfterPage = () => {
      timeoutId = window.setTimeout(() => {
        window.TallyConfig = window.TallyConfig || {};
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          "gtm.start": new Date().getTime(),
          event: "gtm.js",
        });

        addScript("https://www.googletagmanager.com/gtm.js?id=GTM-TG9M3CJ");
        addScript("https://tally.so/widgets/embed.js");
        addScript(
          "https://cdn.jsdelivr.net/npm/@justinribeiro/lite-youtube@1/lite-youtube.min.js",
          { type: "module" }
        );
        addScript(
          "https://unpkg.com/ionicons@5.5.2/dist/ionicons/ionicons.esm.js",
          { type: "module" }
        );
        addScript("https://unpkg.com/ionicons@5.5.2/dist/ionicons/ionicons.js", {
          noModule: true,
        });

        window.hj =
          window.hj ||
          function () {
            (window.hj.q = window.hj.q || []).push(arguments);
          };
        window._hjSettings = { hjid: 5320503, hjsv: 6 };
        addScript("https://static.hotjar.com/c/hotjar-5320503.js?sv=6");
      }, 1500);
    };

    if (document.readyState === "complete") {
      loadAfterPage();
    } else {
      window.addEventListener("load", loadAfterPage, { once: true });
    }

    return () => {
      window.removeEventListener("load", loadAfterPage);
      window.clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    if (!logoutRequested && !isLogoutRoute) {
      return;
    }

    const closeLegacySession = () => {
      window.sessionStorage.setItem(LOGOUT_GUARD_KEY, "1");
      window.localStorage.removeItem("loggedAppUser");
      window.localStorage.removeItem("digispeakFreemiumSession");
      setUser(null);
      setIsCheckingFreemiumRedirect(false);
      clearBrowserCookies();

      const cleanUrl = `${window.location.origin}/`;
      window.history.replaceState({}, document.title, cleanUrl);
      window.sessionStorage.clear();
      window.sessionStorage.setItem(LOGOUT_GUARD_KEY, "1");
      setUser(null);
      setIsCheckingFreemiumRedirect(false);
    };

    closeLegacySession();
  }, [isLogoutRoute, logoutRequested]);

  useEffect(() => {
    if (logoutRequested || isLogoutRoute || logoutRedirectGuard) {
      setIsCheckingFreemiumRedirect(false);
      return;
    }

    if (!currentUserToken || !FREEMIUM_URL) {
      setIsCheckingFreemiumRedirect(false);
      return;
    }

    let isCancelled = false;

    const syncFreemiumRedirect = async () => {
      try {
        const response = await getUserMe_v2();
        const parsedData = JSON.parse(response);
        const hasActiveFreemiumPaidTrial =
          parsedData?.hasActiveFreemiumPaidTrial === true ||
          parsedData?.HasActiveFreemiumPaidTrial === true ||
          isTrialAccessState(parsedData);
        const hasUsedFreemiumPaidTrial =
          parsedData?.hasUsedFreemiumPaidTrial === true ||
          parsedData?.HasUsedFreemiumPaidTrial === true;
        const shouldRedirectFreemiumUser =
          hasActiveFreemiumPaidTrial || isFreemiumPlan(parsedData) || currentUserIsFreemium;

        const nextUser = {
          ...(JSON.parse(localStorage.getItem("loggedAppUser") || "null") || {}),
          hasActiveFreemiumPaidTrial,
          hasUsedFreemiumPaidTrial,
          studentPlanName: parsedData?.studentPlan?.name || currentStudentPlanName || null,
          planName: parsedData?.studentPlan?.name || currentPlanName || null,
          studentLevelName: parsedData?.studentLevel?.name || currentStudentLevelName || null,
          subscriptionAccessState:
            parsedData?.subscriptionAccessState || parsedData?.SubscriptionAccessState || null,
          isFreemiumPlan: isFreemiumPlan(parsedData) || currentUserIsFreemium,
        };

        localStorage.setItem("loggedAppUser", JSON.stringify(nextUser));

        if (isCancelled) {
          return;
        }

        setUser(nextUser);

        if (shouldRedirectFreemiumUser) {
          window.location.replace(
            `${FREEMIUM_URL}#token=${encodeURIComponent(nextUser.token)}`
          );
          return;
        }
      } catch (error) {
        console.error("Unable to verify freemium redirect state", error);
      } finally {
        if (!isCancelled) {
          setIsCheckingFreemiumRedirect(false);
        }
      }
    };

    syncFreemiumRedirect();

    return () => {
      isCancelled = true;
    };
  }, [
    currentPlanName,
    currentStudentLevelName,
    currentStudentPlanName,
    currentUserIsFreemium,
    currentUserToken,
    isLogoutRoute,
    logoutRedirectGuard,
    logoutRequested,
  ]);

  if (isCheckingFreemiumRedirect) {
    return null;
  }

  return (
    <>
      <ScrollToAnchor />
      <Suspense fallback={<RouteLoadingFallback />}>
        <Routes>
          <Route
            path="/impersonate/:token"
            element={<ImpersonateLoginPage />}
          />
          {!user ? (
            <>
              <Route path="/" element={<LoginPage />} />
              <Route path="/logout" element={<LoginPage />} />
              <Route path="/password" element={<RecoverPasswordPage />} />
              <Route path="/password/:secret" element={<ResetPasswordPage />} />
              <Route path="/register-freemium" element={<UserRegisterPage />} />
              <Route
                path="/user-register-b2b/:code"
                element={<UserRegisterWithCode />}
              />
              <Route path="/free-test/:type" element={<FreeTest />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          ) : (
            <Route
              path="/*"
              element={<AuthenticatedApp user={user} />}
            />
          )}
        </Routes>
      </Suspense>
    </>
  );
}

export default App;
